/**
 * promotionApi.js
 *
 * Utilities for checking and consuming user promotions in the Post Ad flow.
 *
 * Usage in Post Ad modal:
 *   import { checkUserPromotion, consumePromotionSlot, calculatePromotionPrice } from './promotionApi';
 *
 *   // 1. When user selects a plan, check for promotion:
 *   const promo = await checkUserPromotion('basic');
 *
 *   // 2. After successful ad post, decrement the slot:
 *   await consumePromotionSlot('basic');
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';

function getToken() {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
}

/**
 * Check if the logged-in user has an active promotion for a given plan.
 *
 * @param {string} plan - 'basic' | 'pro' | 'business' | '' (for free plan)
 * @returns {Promise<{ has_promotion, promotion, plan_slots_remaining }>}
 */
export async function checkUserPromotion(plan = '') {
  try {
    const res = await fetch(
      `${API_BASE}/promotions/check/?plan=${encodeURIComponent(plan)}`,
      { headers: { Authorization: `Token ${getToken()}` } }
    );
    if (res.ok) return await res.json();
  } catch (e) {
    console.warn('Promotion check failed:', e);
  }
  return { has_promotion: false, promotion: null, plan_slots_remaining: null };
}

/**
 * Decrement the promotion slot count after a successful ad post.
 * Call this ONLY after the ad has been successfully created.
 *
 * @param {string} plan - 'basic' | 'pro' | 'business'
 * @returns {Promise<{ success, slots_remaining }>}
 */
export async function consumePromotionSlot(plan) {
  try {
    const res = await fetch(`${API_BASE}/promotions/use-slot/`, {
      method:  'POST',
      headers: {
        Authorization:  `Token ${getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ plan }),
    });
    if (res.ok) return await res.json();
  } catch (e) {
    console.warn('consumePromotionSlot failed:', e);
  }
  return { success: false };
}

/**
 * Calculate the final price after applying a promotion.
 *
 * ✅ FIX — Business (and any) slot-only promo with discount_percent = null/0:
 *
 *   The old code tested `promotion.plan_type === 'discounted' && promotion.discount_percent`.
 *   When discount_percent is null or 0 (slot-only promo — no price reduction, just a
 *   free slot usage), that condition was FALSY. The function fell through to the final
 *   return and gave back hasPromo: false.
 *
 *   Consequence: promoApplied was null → promotion state was null → consumePromotionSlot
 *   was never called after posting → Business slot count never decremented.
 *
 *   The fix splits the 'discounted' branch into two cases:
 *     a) discount_percent > 0  → apply price reduction, label shows "X% off"
 *     b) discount_percent is 0 / null but the plan HAS a slot configured (slots_<plan> >= 0
 *        and not null) → slot-only promo; price stays the same but hasPromo = true so
 *        consumePromotionSlot is correctly called after posting.
 *
 * @param {number} originalPrice  - Normal price for the plan (e.g. 500)
 * @param {object} promotion      - The promotion object from checkUserPromotion
 * @param {string} plan           - 'basic' | 'pro' | 'business'
 * @returns {{
 *   finalPrice:     number,
 *   isFree:         boolean,
 *   discountAmount: number,
 *   label:          string,
 *   hasPromo:       boolean   ← true if ANY valid promo applies (slot OR discount OR free)
 * }}
 */
export function calculatePromotionPrice(originalPrice, promotion, plan) {
  // No promo or promo marked invalid by backend → no change
  if (!promotion || !promotion.is_valid) {
    return { finalPrice: originalPrice, isFree: false, discountAmount: 0, label: '', hasPromo: false };
  }

  // ── Slot guard ───────────────────────────────────────────────────────────────
  // If the backend returned slots_<plan> explicitly as 0, the slot is exhausted.
  // null means either "no slot field set" (pure discount, no limit) or "unlimited".
  const slotKey   = `slots_${plan}`;
  const slotsLeft = promotion[slotKey];
  if (slotsLeft !== null && slotsLeft !== undefined && Number(slotsLeft) <= 0) {
    return { finalPrice: originalPrice, isFree: false, discountAmount: 0, label: '⚠️ Slots used up', hasPromo: false };
  }

  // ── Free / Re-post promo ─────────────────────────────────────────────────────
  if (promotion.plan_type === 'free' || promotion.plan_type === 'repost') {
    return {
      finalPrice:     0,
      isFree:         true,
      discountAmount: originalPrice,
      label:          promotion.plan_type === 'repost' ? '🔄 Re-Post (Free)' : '🎁 Free Posting',
      hasPromo:       true,
    };
  }

  // ── Discounted promo ─────────────────────────────────────────────────────────
  if (promotion.plan_type === 'discounted') {
    const pct      = Number(promotion.discount_percent) || 0;
    const discount = Math.round(originalPrice * (pct / 100));

    if (pct > 0) {
      // Case a: genuine price reduction
      return {
        finalPrice:     originalPrice - discount,
        isFree:         false,
        discountAmount: discount,
        label:          `🏷️ ${pct}% discount applied`,
        hasPromo:       true,
      };
    }

    // ✅ FIX — Case b: slot-only promo (discount_percent is 0 or null).
    // The user has a Business (or any plan) slot that lets them post without
    // paying extra — the slot itself is the benefit, not a price reduction.
    // We MUST set hasPromo: true so that:
    //   • promoApplied is non-null in PlanSelectionModal → passed to PostAdModal
    //   • promotion state is non-null in PostAdModal
    //   • consumePromotionSlot() is called after posting → slot decrements ✅
    //
    // Price stays the same (no monetary discount), but we flag it as a promo
    // so the slot tracking machinery fires correctly.
    return {
      finalPrice:     originalPrice,
      isFree:         false,
      discountAmount: 0,
      label:          '🎫 Promo slot applied',   // shown in UI under plan card
      hasPromo:       true,                       // ← THE KEY FIX
    };
  }

  // ── Premium upgrade ──────────────────────────────────────────────────────────
  if (promotion.plan_type === 'premium') {
    return {
      finalPrice:     0,
      isFree:         true,
      discountAmount: originalPrice,
      label:          '⭐ Premium upgrade applied',
      hasPromo:       true,
    };
  }

  // Unknown plan_type — no promo applied
  return { finalPrice: originalPrice, isFree: false, discountAmount: 0, label: '', hasPromo: false };
}



















// /**
//  * promotionApi.js
//  * 
//  * Call these from your Post Ad modal before showing pricing
//  * to check if the logged-in user has an active promotion.
//  * 
//  * Usage in Post Ad modal:
//  *   import { checkUserPromotion, consumePromotionSlot } from './promotionApi';
//  * 
//  *   // 1. When user selects a plan, check for promotion:
//  *   const promo = await checkUserPromotion('basic');
//  *   if (promo.has_promotion) {
//  *     if (promo.promotion.plan_type === 'free') {
//  *       // Show "FREE" price, hide payment
//  *     } else if (promo.promotion.plan_type === 'discounted') {
//  *       // Apply discount: price * (1 - discount_percent/100)
//  *     }
//  *     if (promo.plan_slots_remaining !== null && promo.plan_slots_remaining <= 0) {
//  *       // Slots used up — charge normally
//  *     }
//  *   }
//  * 
//  *   // 2. After successful ad post, decrement the slot:
//  *   await consumePromotionSlot('basic');
//  */

// const API_BASE = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';

// function getToken() {
//   return localStorage.getItem('authToken') || localStorage.getItem('token');
// }

// /**
//  * Check if the logged-in user has an active promotion for a given plan.
//  * Call this when the user selects a plan in the Post Ad modal.
//  *
//  * @param {string} plan - 'basic' | 'pro' | 'business' | '' (for free plan)
//  * @returns {Promise<{
//  *   has_promotion: boolean,
//  *   promotion: object|null,
//  *   plan_slots_remaining: number|null
//  * }>}
//  */
// export async function checkUserPromotion(plan = '') {
//   try {
//     const res = await fetch(
//       `${API_BASE}/promotions/check/?plan=${encodeURIComponent(plan)}`,
//       { headers: { Authorization: `Token ${getToken()}` } }
//     );
//     if (res.ok) return await res.json();
//   } catch (e) {
//     console.warn('Promotion check failed:', e);
//   }
//   return { has_promotion: false, promotion: null, plan_slots_remaining: null };
// }

// /**
//  * Decrement the promotion slot count after a successful ad post.
//  * Call this ONLY after the ad has been successfully created.
//  *
//  * @param {string} plan - 'basic' | 'pro' | 'business'
//  * @returns {Promise<{ success: boolean, slots_remaining: number }>}
//  */
// export async function consumePromotionSlot(plan) {
//   try {
//     const res = await fetch(`${API_BASE}/promotions/use-slot/`, {
//       method:  'POST',
//       headers: {
//         Authorization:  `Token ${getToken()}`,
//         'Content-Type': 'application/json',
//       },
//       body: JSON.stringify({ plan }),
//     });
//     if (res.ok) return await res.json();
//   } catch (e) {
//     console.warn('consumePromotionSlot failed:', e);
//   }
//   return { success: false };
// }

// /**
//  * Calculate the final price after applying a promotion.
//  * Use in your Post Ad modal to show the user what they'll pay.
//  *
//  * @param {number}  originalPrice  - Normal price for the plan (e.g. 500)
//  * @param {object}  promotion      - The promotion object from checkUserPromotion
//  * @param {string}  plan           - 'basic' | 'pro' | 'business'
//  * @returns {{ finalPrice: number, isFree: boolean, discountAmount: number, label: string }}
//  */
// export function calculatePromotionPrice(originalPrice, promotion, plan) {
//   if (!promotion || !promotion.is_valid) {
//     return { finalPrice: originalPrice, isFree: false, discountAmount: 0, label: '' };
//   }

//   // Check slot availability for this plan
//   const slotKey = `slots_${plan}`;
//   const slotsLeft = promotion[slotKey];
//   if (slotsLeft !== null && slotsLeft !== undefined && slotsLeft <= 0) {
//     return { finalPrice: originalPrice, isFree: false, discountAmount: 0, label: '⚠️ Slots used up' };
//   }

//   if (promotion.plan_type === 'free' || promotion.plan_type === 'repost') {
//     return {
//       finalPrice:     0,
//       isFree:         true,
//       discountAmount: originalPrice,
//       label:          promotion.plan_type === 'repost' ? '🔄 Re-Post (Free)' : '🎁 Free Posting',
//     };
//   }

//   if (promotion.plan_type === 'discounted' && promotion.discount_percent) {
//     const discount = Math.round(originalPrice * (promotion.discount_percent / 100));
//     return {
//       finalPrice:     originalPrice - discount,
//       isFree:         false,
//       discountAmount: discount,
//       label:          `🏷️ ${promotion.discount_percent}% discount applied`,
//     };
//   }

//   if (promotion.plan_type === 'premium') {
//     // Premium upgrade — treat as free for this plan
//     return {
//       finalPrice:     0,
//       isFree:         true,
//       discountAmount: originalPrice,
//       label:          '⭐ Premium upgrade applied',
//     };
//   }

//   return { finalPrice: originalPrice, isFree: false, discountAmount: 0, label: '' };
// }

